// =============================================================================
// CENTRAL EVENT CONFIGURATION — single source of truth for ALL editable content
// Affinity Group · CPF Retirement Planning Seminar
// Edit dates, times, venue, speakers, agenda, partners, links, legal copy here.
// =============================================================================

export const eventConfig = {
  eventId: "cpf-oct-2026",
  // Published domain. Used for adviser share links / QR codes so they always
  // point at the live site even when the dashboard is opened on a staging URL.
  publicSiteUrl: "https://retire.affinitygroup.sg",

  brand: {
    name: "Affinity Group",
    tagline: "A Group Representing Infinity Financial Advisory",
    logo: "/affinity-logo.png",
    contactEmail: "marketing@affinitygroup.sg",
    whatsappNumber: "6589685560", // wa.me format (country code + number, no +)
    whatsappDisplay: "+65 8968 5560",
    whatsappMessage: "Hi, I'm enquiring about the upcoming CPF & Your Retirement Seminar (1 October 2026)",
    footerBlurb: "Helping individuals and families better understand their protection, long-term care and financial planning needs.",
    socials: [
      { label: "Instagram", url: "https://www.instagram.com/affinity.infinityfa/", icon: "instagram" },
      { label: "Facebook", url: "https://www.facebook.com/affinitygroup.sg", icon: "facebook" },
      { label: "LinkedIn", url: "https://linkedin.com/company/infinity-affinity-group", icon: "linkedin" },
      { label: "TikTok", url: "https://www.tiktok.com/@affinitygroup.sg", icon: "tiktok" },
      { label: "YouTube", url: "https://www.youtube.com/@affinitygroupsg", icon: "youtube" },
    ],
  },

  event: {
    title: "CPF Retirement Planning Seminar",
    // ISO date/time (Singapore, UTC+8) used for calendar + countdown logic
    dateISO: "2026-10-01",
    startISO: "2026-10-01T19:00:00+08:00",
    endISO: "2026-10-01T21:00:00+08:00",
    dateLabel: "Thursday, 1 October 2026",
    dateLabelShort: "1 October 2026",
    timeLabel: "7:00PM \u2013 9:00PM",
    registrationTime: null, // TBC — e.g. "6:15PM"
    admission: "Complimentary",
    // Venue: set to a string once confirmed, otherwise null shows placeholder
    venueName: "Capital Group",
    venue: "Capital Group, 1 Raffles Quay, #43 North Tower, Singapore 048583",
    venueLine1: "Capital Group",
    venueLine2: "1 Raffles Quay, #43 North Tower",
    venueLine3: "Singapore 048583",
    mapQuery: "1 Raffles Quay, North Tower, Singapore 048583",
    mapEmbedUrl: "https://www.google.com/maps?q=1%20Raffles%20Quay%20North%20Tower%20Singapore%20048583&output=embed",
    mapLinkUrl: "https://www.google.com/maps/search/?api=1&query=1%20Raffles%20Quay%20North%20Tower%20Singapore%20048583",
    venuePlaceholder: "Venue details coming soon",
    venueSuccessPlaceholder: "Venue details will be sent once confirmed",
    // Registration deadline for a real countdown; null = no countdown shown
    registrationCloseISO: null,
  },

  // -- Confirmation flags. Flip to true only when compliance confirms. --
  flags: {
    cpfBoardRepConfirmed: false,
    partnersConfirmed: false,
    testimonialsApproved: false,
    showCountdown: false, // only true when event.registrationCloseISO is real
  },

  // -- Promo video (Vimeo, 9:16 vertical, 92s) — used in the hero popup and the "Watch" section --
  video: {
    title: "CPF Seminar 2026",
    durationLabel: "90 sec",
    vimeoId: "1213698376",
    vimeoHash: "9bb78dd992",
    embedUrl:
      "https://player.vimeo.com/video/1213698376?h=9bb78dd992&badge=0&autopause=0&player_id=0&app_id=58479",
    shareUrl: "https://vimeo.com/1213698376/9bb78dd992",
    eyebrow: "Watch \u00b7 90 seconds",
    heading: "Sound familiar?",
    supporting:
      "Nicholas asks Alicia four everyday CPF questions over lunch. Most Singaporeans can answer the first one. Almost nobody can answer the rest.",
    // Alicia's questions from the video, in order
    questions: [
      { q: "What happens to your CPF when you turn 55?", hint: "You can withdraw some of it \u2014 but how much, and what happens to the rest?" },
      { q: "What\u2019s the difference between BRS, FRS and ERS?", hint: "Important, yes. But can you explain what each one means for your payouts?" },
      { q: "Which CPF LIFE plan are you on?", hint: "Basic, Standard or Escalating \u2014 they pay out very differently over 30 years." },
      { q: "Will your income last if you stop working for 30 to 40 years?", hint: "One day your salary stops. Your retirement income has to keep going." },
    ],
    stat: {
      value: "1,500",
      label: "Singapore residents aged 100 and over in 2025 \u2014 up from 1,200 in 2015.",
      sourceLabel: "The Straits Times, Feb 2026",
      sourceUrl:
        "https://www.straitstimes.com/singapore/theyre-100-and-full-of-life-super-seniors-defy-expectations-at-active-ageing-centres",
      caption: "Retirement could last 30 or even 40 years. That is what the seminar is designed to prepare you for.",
    },
    // What the seminar covers (as promised in the video)
    covers: [
      "What actually happens to your CPF at age 55",
      "The difference between BRS, FRS and ERS",
      "How CPF LIFE works \u2014 Basic, Standard and Escalating plans",
      "The latest CPF updates (as of 2026) from an official CPF Board representative",
      "How CPF complements your entire retirement portfolio, with Nicholas Lee",
    ],
    ctaLabel: "Reserve My Complimentary Seat",
    watchLabel: "Watch the 90-sec video",
    // Adviser WhatsApp share message (dashboard "Share the video" card).
    // Placeholders: {video} {link} {date} {time} {venue}
    shareMessage:
      "Hi! Quick question: do you know what happens to your CPF at 55?\n\nWatch this 90-second clip:\n{video}\n\nThen join us at our complimentary CPF Retirement Planning Seminar on {date}, {time} at {venue}. A CPF Board representative will share the latest 2026 updates and Nicholas Lee will cover how CPF fits your whole retirement plan.\n\nReserve your seat here (seats are limited):\n{link}",
  },

  hero: {
    eyebrow: "Complimentary CPF Retirement Planning Seminar",
    headlineLine1: "You\u2019ve Contributed to CPF for Years.",
    headlineLine2: "But Do You Know What Happens at 55?",
    supporting:
      "Most Singaporeans know they can withdraw some CPF at 55. But what happens to the rest? And one day, your salary stops \u2014 will your retirement income be enough for the next 20, 30 or even 40 years?",
    ctaLabel: "Reserve My Complimentary Seat",
    ctaSupporting: "Complimentary admission \u00b7 CPF Board representative \u00b7 Practical examples \u00b7 Q&A",
    rings: [
      { label: "Key age", value: "55" },
      { label: "Retirement sums", value: "BRS \u00b7 FRS \u00b7 ERS" },
      { label: "Monthly payouts", value: "CPF LIFE" },
      { label: "Your questions", value: "Answered live" },
    ],
  },

  trustStrip: [
    { title: "Practical. Not overwhelming." },
    { title: "Educational. No jargon." },
    { title: "Interactive. Ask questions live." },
  ],
  // Only rendered when flags.cpfBoardRepConfirmed is true
  trustCpfNote: "Featuring an official CPF Board sharing and live Q&A.",

  quiz: {
    title: "How CPF-Ready Are You?",
    questions: [
      "I know what happens to my CPF accounts at age 55.",
      "I understand the difference between BRS, FRS and ERS.",
      "I know how the three CPF LIFE plans differ.",
    ],
    answers: ["Yes", "Not Yet"],
    ctaLabel: "Reserve a Seat",
  },

  learn: {
    heading: "Leave With Clarity, Not More Confusion.",
    supporting: "Five things you will walk away understanding \u2014 explained in plain English.",
    cards: [
      {
        title: "What actually happens to your CPF at 55?",
        body: "How your Retirement Account is formed, what you may withdraw and what happens to the rest of your CPF.",
      },
      {
        title: "BRS, FRS or ERS \u2014 what do they mean for you?",
        body: "See how the retirement sums act as reference points for your desired lifelong monthly payouts.",
      },
      {
        title: "How does CPF LIFE work?",
        body: "Compare the Basic, Standard and Escalating plans \u2014 and find out which one you are on.",
      },
      {
        title: "What has changed in CPF as of 2026?",
        body: "The latest CPF updates, explained by an official CPF Board representative.",
      },
      {
        title: "How does CPF fit your whole retirement plan?",
        body: "Nicholas Lee on how CPF can complement the rest of your retirement portfolio.",
      },
    ],
  },

  myths: {
    heading: "CPF Myth or Fact?",
    supporting: "Test what you think you know before the seminar.",
    cards: [
      {
        statement: "I can withdraw all my CPF savings when I turn 55.",
        answer: "Myth",
        explanation:
          "The amount available depends on the retirement sum set aside and other withdrawal conditions. The seminar will walk through the common scenarios.",
      },
      {
        statement: "All CPF LIFE plans provide the same payout pattern.",
        answer: "Myth",
        explanation:
          "Basic, Standard and Escalating plans provide different payout patterns.",
      },
      {
        statement: "I can choose to start my payouts later than age 65.",
        answer: "Fact",
        explanation:
          "CPF retirement payouts can generally start between ages 65 and 70. Starting later may result in higher monthly payouts.",
      },
    ],
    sourceNote:
      "Educational preview based on publicly available CPF Board information. Information should be verified and compliance-approved before publication.",
    sourceLinks: [
      {
        label: "CPF withdrawals for immediate retirement needs",
        url: "https://www.cpf.gov.sg/member/retirement-income/retirement-withdrawals/withdrawing-for-immediate-retirement-needs",
      },
      {
        label: "CPF LIFE monthly payouts",
        url: "https://www.cpf.gov.sg/member/retirement-income/monthly-payouts/cpf-life",
      },
    ],
  },

  timeline: {
    heading: "Three Ages. Different Decisions.",
    officialLinkLabel: "Read the official CPF Board overview",
    officialLinkUrl: "https://www.cpf.gov.sg/member/retirement-income",
    ages: [
      {
        age: "55",
        title: "A Key CPF Milestone",
        description:
          "Your Retirement Account is created, and your withdrawal options depend on how much retirement savings have been set aside.",
        question: "What can I withdraw, keep growing or top up?",
      },
      {
        age: "65",
        title: "Decide When Payouts Begin",
        description:
          "This is generally when monthly retirement payouts can begin. Your plan and savings affect the payout pattern and amount.",
        question: "Which payout option supports my lifestyle?",
      },
      {
        age: "70",
        title: "The Deferral Decision",
        description:
          "You may choose to start later, up to age 70. Deferring is one of the decisions worth understanding before you commit.",
        question: "Would delaying my payouts make sense for me?",
      },
    ],
  },

  agenda: {
    heading: "A Focused Two-Hour Session.",
    items: [
      {
        time: "6:15PM",
        title: "Registration & Refreshments",
        body: "Arrive early, settle in and meet the team.",
      },
      {
        time: "7:00PM",
        title: "CPF & Your Retirement",
        body: "An official CPF Board representative walks through age 55, BRS/FRS/ERS, CPF LIFE and the latest 2026 updates.",
      },
      {
        time: "7:45PM",
        title: "Refine Your Retirement Portfolio",
        body: "Nicholas Lee on how CPF complements your entire retirement plan.",
      },
      {
        time: "8:30PM",
        title: "Investing for the Long Term: Opportunities Across Global Equities",
        body: "Suyin Tan (Capital Group) on CPF OA-approved global equity strategies for long-term capital growth.",
      },
      {
        time: "8:45PM",
        title: "Live Q&A",
        body: "Ask the questions that matter to you and your family.",
      },
    ],
  },

  speakers: {
    heading: "Learn From People Who Explain Clearly.",
    list: [
      {
        name: "CPF Board Representative",
        role: "Communications & Engagement",
        rnf: null,
        status: "To Be Confirmed",
        bio: null,
        photo: null, // do not invent a photo
        confirmed: false,
      },
      {
        name: "Nicholas Lee",
        role: "Senior Associate Director (Financial Services) representing Infinity Financial Advisory",
        rnf: "LCW300188187",
        status: "Returning Speaker \u00b7 To Confirm",
        bio: "Nicholas Lee is a Certified Financial Planner with more than 12 years of experience helping individuals and families understand their retirement-planning choices.",
        photo: null, // supply official photo here when provided
        confirmed: false,
      },
    ],
  },

  proof: {
    smallLabel: "Highlights from our past events",
    heading: "See What Our Past CPF Seminars Were Like",
    supporting:
      "Watch highlights from our previous seminars and discover the learning experience you can expect at our upcoming session.",
    events: [
      {
        number: "01",
        title: "CPF Seminar 2025",
        shortTitle: "CPF Seminar 2025",
        description:
          "Watch the highlights and attendee experiences from our CPF Retirement Planning Seminar in 2025.",
        src: "https://player.vimeo.com/video/1158275324?h=ccfbbfb02a&badge=0&autopause=0&player_id=0&app_id=58479",
      },
      {
        number: "02",
        title: "CPF Seminar \u2014 March 2026",
        shortTitle: "CPF March 2026",
        description:
          "See the conversations, learning moments and attendee experience from our March 2026 CPF Seminar.",
        src: "https://player.vimeo.com/video/1187224725?h=ddb5237ff9&badge=0&autopause=0&player_id=0&app_id=58479",
      },
      {
        number: "03",
        title: "CPF Seminar \u2014 August 2026",
        shortTitle: "CPF August 2026",
        description:
          "Experience the energy and key moments from our most recent CPF Seminar held in August 2026.",
        src: "https://player.vimeo.com/video/1221040793?h=0c2219de4b&badge=0&autopause=0&player_id=0&app_id=58479",
      },
    ],
    ctaTitle: "Join Us at the Next CPF Seminar",
    ctaButton: "Reserve My Complimentary Seat",
  },

  registration: {
    heading: "Get Clear on Your CPF Retirement Decisions.",
    supporting:
      "Join us on Thursday, 1 October for a complimentary evening built around the questions Singaporeans actually ask \u2014 what happens at 55, what the retirement sums mean, and how CPF LIFE pays you for life.",
    inclusions: [
      "Complimentary admission",
      "Refreshments provided",
      "Live question-and-answer session",
      "Practical retirement checklist",
    ],
    ageRanges: ["21\u201335", "36\u201345", "46\u201355", "56\u201365", "Above 65"],
    // Tappable example prompts to encourage filling the "Biggest CPF Question" field
    questionPrompts: [
      "What happens to the rest of my CPF after 55?",
      "How much can I withdraw at 55?",
      "What\u2019s the difference between BRS, FRS and ERS?",
      "Which CPF LIFE plan am I on?",
      "Will my income last 30 to 40 years?",
      "Should I top up my Retirement Account?",
    ],
    successHeading: "Your Seat Is Reserved",
    successSupporting:
      "We\u2019ll send your registration confirmation and event updates to the contact details you provided.",
    whatNext: [
      "Look out for a confirmation from our team.",
      "Add the seminar to your calendar so you don\u2019t miss it.",
      "Bring your CPF questions for the live Q&A.",
    ],
  },

  finalCta: {
    heading: "One Day Your Salary Stops. Will Your Retirement Income Keep Going?",
    ctaLabel: "Reserve My Complimentary Seat",
  },

  faq: [
    {
      q: "Is this seminar complimentary?",
      a: "Yes. Admission is complimentary, but registration is required as seating is limited by the venue capacity.",
      category: "registration",
    },
    {
      q: "What will the seminar cover?",
      a: "In two hours you will learn what actually happens to your CPF at age 55, the difference between BRS, FRS and ERS, how CPF LIFE and its three plans work, the latest CPF updates (as of 2026) from an official CPF Board representative, and practical insights from Nicholas Lee on how CPF can complement your entire retirement portfolio. A live Q&A closes the evening.",
      category: "programme",
    },
    {
      q: "Who is this seminar suitable for?",
      a: "Anyone who has been contributing to CPF and wants to know what happens next. It is especially useful if you are approaching 55, deciding on CPF LIFE at 65, or planning early in your 30s and 40s so your retirement income can last 30 to 40 years. Family members planning together are very welcome.",
      category: "registration",
    },
    {
      q: "Will a CPF Board representative be present?",
      a: "Yes. Angeline Teo, Assistant Director from CPF Board\u2019s Communications & Engagement Group, will open the evening with the CPF retirement journey session and take part in the live Q&A. Any change to the line-up will be reflected on this page and in your event emails.",
      category: "programme",
    },
    {
      q: "When can CPF monthly payouts start?",
      a: "You can choose to start monthly payouts anytime from age 65 to 70, and can submit your decision up to three months before turning 65. If you have not started them earlier, payouts begin automatically at age 70. Deferring increases what you receive later: CPF LIFE payouts grow by up to 7% for each year deferred (up to 35% at 70). If you are not on CPF LIFE, deferring gives higher payouts or a longer payout duration.",
      category: "cpf",
      link: {
        label: "Verify on CPF Board",
        url: "https://www.cpf.gov.sg/member/retirement-income/milestones/reaching-age-65",
      },
    },
    {
      q: "Can I ask questions during the seminar?",
      a: "Yes. A live question-and-answer segment is included at the end of the session, so you can ask the questions that matter to you and your family.",
      category: "programme",
    },
    {
      q: "Will refreshments be provided?",
      a: "Yes. Light refreshments will be provided. Arrive early to settle in and meet the team.",
      category: "programme",
    },
    {
      q: "Where will the seminar be held?",
      a: "The seminar will be held at Capital Group, 1 Raffles Quay, #43 North Tower, Singapore 048583. Full directions will be sent with your confirmation.",
      category: "venue",
    },
    {
      q: "What if I cannot attend after registering?",
      a: "No problem \u2014 simply let us know by replying to your confirmation or emailing marketing@affinitygroup.sg.",
      category: "registration",
    },
  ],

  partners: {
    // Supporting partners are To Be Confirmed for this event.
    confirmed: false,
    placeholder: "Supporting partners to be confirmed",
    logos: [], // add { name, url } once partners are officially confirmed
  },

  // ---------------------------------------------------------------------------
  // LEGAL / COMPLIANCE — editable block, labelled for compliance review.
  // >>> COMPLIANCE REVIEW REQUIRED before publication. <<<
  // ---------------------------------------------------------------------------
  legal: {
    privacyNote:
      "By submitting this form, you consent to being contacted regarding your registration and event updates.",
    // Full PDPA consent disclaimer shown before every form submission button.
    dataConsentDisclaimer: {
      intro:
        "By providing us your personal data (i.e. name, email address and phone number), you acknowledge and consent to our collection of your personal data for the purposes listed below:",
      purposes: [
        "Sending you marketing, advertising or promotional materials related to the content of this website, whether by call, text or email;",
        "Provision of products & services which you have requested for.",
      ],
      withdrawal:
        "Please note that you are entitled to withdraw your consent for the collection of your personal data at any point in time by providing a notification to marketing@affinitygroup.sg.",
    },
    marketingConsentLabel:
      "I would also like to receive occasional educational content, event invitations and updates from Affinity Group. (Optional)",
    footerLinks: [
      { label: "Privacy Policy", url: "/privacy" },
      { label: "Terms of Use", url: "/terms" },
    ],
    masStatement:
      "This advertisement has not been reviewed by the Monetary Authority of Singapore.",
    disclaimers: [
      "This site is not a part of the Facebook website or Facebook Inc. Additionally, this site is NOT endorsed by Facebook in any way. FACEBOOK is a trademark of FACEBOOK, Inc. Facebook provides a platform to deliver advertising, but the opinions and/or views expressed by Affinity Group representing Infinity Financial Advisory Pte Ltd are not representative of Facebook, Inc.",
      "Personal Data Protection Act: By registering for this event and submitting your personal information, you consent to being contacted by Affinity Group, A Group Representing Infinity Financial Advisory Pte Ltd, and participating event partners and sponsors for purposes including event registration confirmation and updates, follow-up regarding products and services discussed during the event, marketing, promotional and educational communications, and invitations to future events, seminars and workshops. You may withdraw your consent for the collection of your personal data at any time by emailing marketing@affinitygroup.sg.",
      "Disclaimer: The information contained in this website is provided to you for general information/circulation only and is not intended to nor will it create/induce the creation of any binding legal relations. The information or opinions provided do not constitute investment advice, a recommendation, an offer or solicitation to subscribe for, purchase or sell the investment product(s) mentioned herein. It does not have any regard to your specific investment objectives, financial situation and any of your particular needs. Accordingly, no warranty whatsoever is given and no liability whatsoever is accepted for any loss arising whether directly or indirectly as a result of any person or group of persons acting on this information. Investments are subject to investment risks including possible loss of the principal amount invested. The value of the product and the income from them may fall as well as rise.",
      "You should seek advice from a financial adviser regarding the suitability of the investment products mentioned, taking into account your specific investment objectives, financial situation or particular needs, before making a commitment to purchase the investment product. In the event that you choose not to obtain advice from a financial adviser, you should assess and consider whether the investment product is suitable for you before proceeding to invest.",
    ],
    // Label so compliance can quickly find and replace copy
    reviewLabel: "[FOR COMPLIANCE REVIEW] Placeholder legal wording adapted from the previous seminar page. Verify and approve before publishing.",
  },

  footer: {
    quickLinks: [
      { label: "Watch", href: "#watch" },
      { label: "Stories", href: "#past-events" },
      { label: "Agenda", href: "#agenda" },
      { label: "Speakers", href: "#speakers" },
      { label: "FAQ", href: "#faq" },
      { label: "Reserve a Seat", href: "#register" },
    ],
    partnersIntro: "For this CPF Seminar, our supporting partners are:",
    partners: [
      // Official CPF Board logo (green, for white tiles) - approved by CPF Board.
      { name: "Central Provident Fund Board", logo: "/partners/cpf-board.png" },
      { name: "Financial Planning Association of Singapore (FPAS)", logo: "/partners/fpas.png" },
      { name: "Capital Group", logo: "https://images.contentstack.io/v3/assets/blt57caa63e0368f6e8/blt31e08877cfb6ac56/60708ec3fdb1ac4cff0aaa81/CaptialGroup_Logo_Horizontal.png" },
      { name: "Singapore PainCare TCM Wellness", logo: "https://sgpaincare.com/wp-content/uploads/2025/04/About-Group-TCM-01.svg" },
      { name: "Tokio Marine Insurance Group", logo: "/partners/tokio-marine.png" },
      { name: "STARBALM", logo: "https://ycb.vn/wp-content/uploads/2018/07/starbalm-logo.png.webp" },
      { name: "AzureGym", logo: "/partners/azuregym.png" },
    ],
  },

  seo: {
    title: "CPF Retirement Planning Seminar | 1 October 2026",
    description:
      "You\u2019ve contributed to CPF for years \u2014 but do you know what happens at 55? Learn what happens to your CPF, the difference between BRS, FRS and ERS, how CPF LIFE works and the latest 2026 CPF updates from a CPF Board representative. Complimentary seminar, 1 October 2026.",
  },

  // ---------------------------------------------------------------------------
  // ADVISERS — each adviser gets a personalised referral link: /?ref=<code>
  // Add or edit advisers here; the "Invited By" field auto-detects the code.
  // ---------------------------------------------------------------------------
  advisers: [
    { code: "IFA0319", name: "Manickaratnam Ratheshan" },
    { code: "IFA0305", name: "Ryan Seow Kian Hong" },
    { code: "IFA0298", name: "Leslie Chia See Sin" },
    { code: "IFA0291", name: "Loh Yi Wei" },
    { code: "IFA0281", name: "Adrian Khiat Han Hwee" },
    { code: "IFA0273", name: "Ting Suk Kiong" },
    { code: "IFA0232", name: "Jacob Gooi Cheng Siong" },
    { code: "IFA0200", name: "Nicholas Lee Chien Wei" },
    { code: "IFA0195", name: "Jason Tan Choon Seng" },
    { code: "IFA0154", name: "Biona Sheila Rey" },
    { code: "IFA0145", name: "Fion Yong Yam Foon" },
    { code: "IFA0142", name: "Liew Teck Hong" },
    { code: "IFA0121", name: "Corrine Sim Yew Hoon" },
    { code: "IFA0088", name: "Travis Teo Jie Hua" },
    { code: "IFA0072", name: "Joseph Yong Kuan Loong" },
    { code: "IFA0068", name: "Derrick Teo Chiaw Ming" },
    { code: "IFA0048", name: "Justin Chew Choon Choon" },
    { code: "IFA0025", name: "Adele Ang Swee Hahn" },
    { code: "IFA0020", name: "Shanne Wong Sook Kwan" },
    { code: "IFA0019", name: "Chua Wee Teck" },
    { code: "IFA0017", name: "Tang Sok Hui Mrs Karen Wadia" },
    { code: "IFA0013", name: "Andy Liu Jinming" },
    { code: "IFA0056", name: "Ng Jian Da" },
    { code: "IFA0059", name: "Vincent Chua Tin An" },
    { code: "IFA0351", name: "Foo Siew Suan Shannon" },
    { code: "IFA0422", name: "Scott Tang Yoon Chong" },
    { code: "IFA0414", name: "Ng Wee Keong Ken" },
    { code: "IFA0484", name: "Karen Ang Pui Ling" },
    // Partner & team links
    { code: "spt01", name: "Singapore Paincare TCM Wellness & Clinic" },
    { code: "cg01", name: "Capital Group" },
    { code: "FPAS", name: "Financial Planning Association of Singapore" },
    { code: "ac01", name: "Affinity Core Team" },
    { code: "ia01", name: "Infinity ARK" },
  ],
};

export default eventConfig;
